import { useSatelliteWebLandlordWarningsHandler_getUserWarnings } from '@zg-rentals/ts-satellite-client-react-query';
import type { SatelliteWebLandlordUserWarningsResponseDto } from '@zg-rentals/ts-satellite-client';
import {
  type UseUserWarningsOptions as SharedUseUserWarningsOptions,
  type UseUserWarningsReturn as SharedUseUserWarningsReturn,
  USER_WARNINGS_QUERY_CONFIG,
  useSharedUserWarnings,
} from '@zg-rentals/satellite-components';
import { rentalClient } from '../httpClient';

export type UseUserWarningsOptions = SharedUseUserWarningsOptions;

export type UseUserWarningsReturn = SharedUseUserWarningsReturn & {
  isLoading: boolean;
  error: unknown;
  rawData?: SatelliteWebLandlordUserWarningsResponseDto;
};

/**
 * Custom hook to fetch and process user warnings data for satellite-landlord-web
 * Combines React Query data fetching with shared processing logic
 * Cache for 1 week since this data rarely changes
 * Only make API call when feature flag is enabled
 */
export const useUserWarnings = ({
  enablePersonalContactWarning = false,
  enableExternalLinkWarning = false,
}: UseUserWarningsOptions = {}): UseUserWarningsReturn => {
  const {
    data: userWarningsData,
    isLoading,
    error,
  } = useSatelliteWebLandlordWarningsHandler_getUserWarnings({
    httpClient: rentalClient,
    ...USER_WARNINGS_QUERY_CONFIG,
    enabled: Boolean(enablePersonalContactWarning || enableExternalLinkWarning),
  });

  const { personalContactWarningData, externalLinkWarningData } = useSharedUserWarnings({
    enablePersonalContactWarning,
    enableExternalLinkWarning,
    userWarningsData,
  });

  return {
    personalContactWarningData,
    externalLinkWarningData,
    isLoading,
    error,
    rawData: userWarningsData,
  };
};
